import { Component, ElementRef, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { TranslationService } from '../../core/i18n/translation.service';

export interface MultiSelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  host: {
    class: 'app-multiselect',
    '(document:click)': 'closeOutside($event)',
    '(keydown.escape)': 'closeWithKeyboard($event)',
    '(focusout)': 'closeOnFocusOut($event)',
  },
})
export class MultiSelectComponent {
  readonly controlId = input.required<string>();
  readonly label = input.required<string>();
  readonly placeholder = input.required<string>();
  readonly options = input<MultiSelectOption[]>([]);
  readonly selectedValues = input<string[]>([]);
  readonly disabled = input(false);
  readonly selectionChanged = output<string[]>();
  protected readonly i18n = inject(TranslationService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');
  protected readonly isOpen = signal(false);
  protected readonly selectedSet = computed(() => new Set(this.selectedValues()));
  protected readonly selectedOptions = computed(() => this.options().filter(option => this.selectedSet().has(option.value)));
  protected readonly allSelected = computed(() => this.options().length > 0 && this.selectedOptions().length === this.options().length);
  protected readonly partiallySelected = computed(() => this.selectedOptions().length > 0 && !this.allSelected());

  constructor() {
    effect(() => {
      if (this.disabled()) this.isOpen.set(false);
    });
  }

  protected toggle(): void {
    if (!this.disabled()) this.isOpen.update(open => !open);
  }

  protected toggleOption(value: string): void {
    if (this.disabled()) return;
    const selected = new Set(this.selectedValues());
    if (selected.has(value)) selected.delete(value);
    else selected.add(value);
    this.selectionChanged.emit(this.options().filter(option => selected.has(option.value)).map(option => option.value));
  }

  protected toggleAll(): void {
    if (!this.disabled()) this.selectionChanged.emit(this.allSelected() ? [] : this.options().map(option => option.value));
  }

  protected closeOutside(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) this.isOpen.set(false);
  }

  protected closeWithKeyboard(event: Event): void {
    if (!this.isOpen()) return;
    event.stopPropagation();
    event.preventDefault();
    this.isOpen.set(false);
    this.trigger()?.nativeElement.focus();
  }

  protected closeOnFocusOut(event: FocusEvent): void {
    if (event.relatedTarget && !this.host.nativeElement.contains(event.relatedTarget as Node)) this.isOpen.set(false);
  }
}
